import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { ArrowLeft, BookOpen, ClipboardList, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import Navbar from "@/components/Navbar";

const fadeUp = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4 } };

const colorMap = {
  maths: { gradient: "from-blue-500 to-blue-700", light: "bg-blue-500/10", text: "text-blue-400", border: "border-blue-500/30" },
  science: { gradient: "from-emerald-500 to-emerald-700", light: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-500/30" },
  social: { gradient: "from-orange-500 to-orange-700", light: "bg-orange-500/10", text: "text-orange-400", border: "border-orange-500/30" },
  english: { gradient: "from-rose-500 to-rose-700", light: "bg-rose-500/10", text: "text-rose-400", border: "border-rose-500/30" },
  hindi: { gradient: "from-violet-500 to-violet-700", light: "bg-violet-500/10", text: "text-violet-400", border: "border-violet-500/30" },
  physics: { gradient: "from-blue-500 to-blue-700", light: "bg-blue-500/10", text: "text-blue-400", border: "border-blue-500/30" },
  chemistry: { gradient: "from-emerald-500 to-emerald-700", light: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-500/30" },
  biology: { gradient: "from-green-500 to-green-700", light: "bg-green-500/10", text: "text-green-400", border: "border-green-500/30" },
  accounts: { gradient: "from-orange-500 to-orange-700", light: "bg-orange-500/10", text: "text-orange-400", border: "border-orange-500/30" },
  business: { gradient: "from-violet-500 to-violet-700", light: "bg-violet-500/10", text: "text-violet-400", border: "border-violet-500/30" },
  economics: { gradient: "from-cyan-500 to-cyan-700", light: "bg-cyan-500/10", text: "text-cyan-400", border: "border-cyan-500/30" },
};

export default function SubjectPage() {
  const { subjectId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [subject, setSubject] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const grade = user?.grade || 6;
  const board = user?.board || "CBSE";
  const stream = user?.stream || "";
  const colors = colorMap[subjectId] || colorMap.maths;

  useEffect(() => { fetchData(); }, [subjectId, grade, board]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let chUrl = `${API}/chapters/${subjectId}/${grade}?board=${board}`;
      if (grade >= 11 && stream) chUrl += `&stream=${stream}`;
      const [subRes, chRes] = await Promise.all([
        axios.get(`${API}/subjects/${subjectId}`),
        axios.get(chUrl)
      ]);
      setSubject(subRes.data);
      setChapters(chRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-blue-500/30 border-t-orange-500 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="subject-page">
      <Navbar />
      <div className={`bg-gradient-to-r ${colors.gradient}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <button data-testid="subject-back-btn" onClick={() => navigate("/home")} className="flex items-center gap-1 text-white/80 hover:text-white text-sm mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </button>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-white">{subject?.name}</h1>
          <p className="text-white/80 mt-2">Grade {grade} | {board} Curriculum</p>
          {(() => {
            const books = [...new Set(chapters.map(ch => ch.reference_book).filter(Boolean))];
            return books.length > 0 && (
              <div className="text-slate-500 text-sm mt-1">
                {books.map((b, i) => (
                  <p key={i} className="flex items-center gap-1.5">
                    <BookOpen className="w-3.5 h-3.5 flex-shrink-0" /> {b}
                  </p>
                ))}
              </div>
            );
          })()}
          <div className="flex gap-3 mt-4">
            <Button data-testid="subject-test-btn" onClick={() => navigate(`/test`)}
              className="rounded-full bg-white/20 hover:bg-white/30 text-white border border-white/30 font-bold active:scale-95">
              <ClipboardList className="w-4 h-4 mr-2" /> Take Test
            </Button>
            <Button data-testid="subject-doubt-btn" onClick={() => navigate("/doubt")}
              className="rounded-full bg-white/20 hover:bg-white/30 text-white border border-white/30 font-bold active:scale-95">
              Ask Doubt
            </Button>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h2 className="font-heading text-xl font-bold text-slate-800 mb-5">Chapters ({chapters.length})</h2>
        {chapters.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-slate-200">
            <BookOpen className="w-12 h-12 text-white/20 mx-auto mb-3" />
            <p className="text-slate-400">No chapters available for this grade yet.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Group chapters by unit or sub-category */}
            {(() => {
              const units = {};
              const sciSubOrder = ["Physics", "Chemistry", "Biology"];
              const hasSub = chapters.some(ch => ch.sub_subject);

              chapters.forEach(ch => {
                let unitKey, unitName;
                if (hasSub) {
                  // ICSE Science: group by sub_subject (Physics/Chemistry/Biology)
                  unitKey = ch.sub_subject || ch.unit || "General";
                  unitName = unitKey;
                } else if (subjectId === "social") {
                  const cats = ["History", "Geography", "Civics", "Economics", "Political Science"];
                  const found = cats.find(c => ch.title?.includes(`(${c})`));
                  unitKey = found || ch.unit || "General";
                  unitName = found || ch.unit || "General";
                } else if (ch.unit) {
                  unitKey = ch.unit;
                  unitName = ch.unit;
                } else {
                  unitKey = "all";
                  unitName = "Chapters";
                }
                if (!units[unitKey]) {
                  units[unitKey] = { name: unitName, chapters: [], minCh: ch.chapter_number || 999 };
                }
                units[unitKey].chapters.push(ch);
                if ((ch.chapter_number || 999) < units[unitKey].minCh) {
                  units[unitKey].minCh = ch.chapter_number || 999;
                }
              });
              const catOrder = ["History", "Geography - Part I: Map Work", "Geography - Part II: Geography of India", "Geography", "Civics", "Political Science", "Economics", "General"];
              let sortedEntries;
              if (hasSub) {
                sortedEntries = Object.entries(units).sort(([a],[b]) => sciSubOrder.indexOf(a) - sciSubOrder.indexOf(b));
              } else if (subjectId === "social") {
                sortedEntries = Object.entries(units).sort(([a],[b]) => catOrder.indexOf(a) - catOrder.indexOf(b));
              } else {
                sortedEntries = Object.entries(units).sort(([,a],[,b]) => a.minCh - b.minCh);
              }
              return sortedEntries.map(([unitKey, unit]) => (
                <div key={unitKey}>
                  {/* Section Header */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${colors.gradient} flex items-center justify-center flex-shrink-0`}>
                      <span className="text-white font-bold text-xs">{unit.name.charAt(0)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading font-bold text-slate-800 text-base sm:text-lg truncate">{unit.name}</h3>
                      <p className="text-slate-500 text-xs">{unit.chapters.length} chapter{unit.chapters.length !== 1 ? "s" : ""}</p>
                    </div>
                    {unit.marks && (
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${colors.light} ${colors.text} flex-shrink-0`}>
                        {unit.marks} marks
                      </span>
                    )}
                  </div>
                  {/* Chapters in this unit */}
                  <div className="space-y-2.5 ml-2 pl-4 border-l-2 border-slate-200">
                    {unit.chapters.map(ch => (
                      <div key={ch.id}
                        className={`bg-white backdrop-blur-sm rounded-2xl p-4 sm:p-5 border ${colors.border} hover:border-orange-500/40 cursor-pointer transition-all duration-200`}
                        onClick={() => navigate(`/chapter/${ch.id}`)} data-testid={`chapter-card-${ch.id}`}>
                        <div className="flex items-center gap-3 sm:gap-4">
                          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${colors.light} flex items-center justify-center flex-shrink-0`}>
                            <span className={`font-heading font-extrabold text-base sm:text-lg ${colors.text}`}>{ch.chapter_number}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-heading font-bold text-slate-800 text-sm sm:text-base truncate">{ch.title}</h3>
                            <div className="flex flex-wrap gap-1.5 mt-1.5">
                              {ch.topics?.slice(0, 3).map(t => (
                                <span key={t} className={`text-xs sm:text-sm px-2.5 py-1 rounded-full ${colors.light} ${colors.text} font-medium`}>{t}</span>
                              ))}
                              {ch.topics?.length > 3 && <span className="text-[10px] sm:text-xs text-slate-300">+{ch.topics.length - 3} more</span>}
                            </div>
                          </div>
                          <ChevronRight className={`w-5 h-5 ${colors.text} flex-shrink-0`} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ));
            })()}
          </div>
        )}
      </main>
    </div>
  );
}
